import React, { useState } from 'react';

const PayrollSection = () => {
  // --- Mock Data (Converted to Rupees) ---
  const initialPayrollData = [
    { id: 1, employeeId: 'E-1001', name: 'James Wilson', department: 'Engineering', position: 'Senior Frontend Dev', salary: 85000, bonus: 5000, deductions: 3200, netPay: 86800, status: 'Paid', payDate: '31-05-2025' },
    { id: 2, employeeId: 'E-1002', name: 'Sarah Johnson', department: 'Marketing', position: 'Marketing Director', salary: 95000, bonus: 7000, deductions: 4100, netPay: 97900, status: 'Paid', payDate: '31-05-2025' },
    { id: 3, employeeId: 'E-1003', name: 'Michael Chen', department: 'Sales', position: 'Account Executive', salary: 72000, bonus: 3500, deductions: 2900, netPay: 72600, status: 'Pending', payDate: '15-06-2025' },
    { id: 4, employeeId: 'E-1004', name: 'Emily Rodriguez', department: 'HR', position: 'HR Generalist', salary: 68000, bonus: 2000, deductions: 2600, netPay: 67400, status: 'Paid', payDate: '31-05-2025' },
    { id: 5, employeeId: 'E-1005', name: 'David Kim', department: 'Engineering', position: 'Backend Lead', salary: 110000, bonus: 10000, deductions: 5000, netPay: 115000, status: 'Processing', payDate: '01-06-2025' },
  ];

  const [payrollData, setPayrollData] = useState(initialPayrollData);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showNewPayrollModal, setShowNewPayrollModal] = useState(false);
  
  // New Payroll Form State
  const [newPayrollForm, setNewPayrollForm] = useState({
    employeeId: '',
    name: '',
    department: '',
    position: '',
    salary: '',
    bonus: '',
    deductions: '',
    status: 'Pending',
    payDate: new Date().toLocaleDateString('en-GB').split('/').join('-') // DD-MM-YYYY format
  });

  // Helper: Format date from YYYY-MM-DD to DD-MM-YYYY for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    // Check if it's already in DD-MM-YYYY format
    if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) return dateString;
    // Convert from YYYY-MM-DD to DD-MM-YYYY
    const parts = dateString.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString;
  };

  // Helper: Convert date from DD-MM-YYYY to YYYY-MM-DD for validation
  const convertToISODate = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString;
  };

  // --- Validation helper functions ---
  const validateTextLength = (value, maxLength) => {
    if (value.length > maxLength) {
      return value.slice(0, maxLength);
    }
    return value;
  };

  const validateNonNegativeNumber = (value) => {
    if (value === '') return '';
    // Remove any non-digit characters for validation
    const cleaned = value.toString().replace(/[^\d]/g, '');
    if (cleaned === '') return '';
    let num = parseInt(cleaned, 10);
    if (isNaN(num)) return '';
    // Limit to 10 digits
    if (cleaned.length > 10) {
      return parseInt(cleaned.slice(0, 10), 10);
    }
    return num < 0 ? 0 : num;
  };

  const validateDate = (dateString) => {
    if (!dateString) return '';
    
    // Validate DD-MM-YYYY format
    const dateRegex = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (!dateRegex.test(dateString)) {
      return new Date().toLocaleDateString('en-GB').split('/').join('-'); // Default to current date in DD-MM-YYYY
    }
    
    const parts = dateString.split('-');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    
    // Check if date is valid
    const dateObj = new Date(year, month - 1, day);
    if (dateObj.getFullYear() !== year || dateObj.getMonth() !== month - 1 || dateObj.getDate() !== day) {
      return new Date().toLocaleDateString('en-GB').split('/').join('-');
    }
    
    // Check year range (2025-2099)
    if (year >= 2025 && year <= 2099) {
      return dateString;
    }
    
    return new Date().toLocaleDateString('en-GB').split('/').join('-');
  };

  // --- Handlers with validation ---
  const handleNewPayrollChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Apply validations based on field
    switch (name) {
      case 'name':
      case 'position':
        processedValue = validateTextLength(value, 100);
        break;
      case 'employeeId':
        processedValue = validateTextLength(value, 15);
        break;
      case 'salary':
      case 'bonus':
      case 'deductions':
        processedValue = validateNonNegativeNumber(value);
        break;
      case 'payDate':
        processedValue = validateDate(value);
        break;
      default:
        break;
    }

    setNewPayrollForm(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleSubmitNewPayroll = () => {
    // Validation
    if (!newPayrollForm.name || !newPayrollForm.employeeId || !newPayrollForm.salary) {
      alert('Please fill in all required fields (Name, Employee ID, and Salary)');
      return;
    }

    // Validate max lengths again before submission
    if (newPayrollForm.name.length > 100) {
      alert('Name must not exceed 100 characters');
      return;
    }
    if (newPayrollForm.position.length > 100) {
      alert('Position must not exceed 100 characters');
      return;
    }
    if (newPayrollForm.employeeId.length > 15) {
      alert('Employee ID must not exceed 15 characters');
      return;
    }

    // Validate non-negative numbers and 10-digit limit
    const salary = parseFloat(newPayrollForm.salary) || 0;
    const bonus = parseFloat(newPayrollForm.bonus) || 0;
    const deductions = parseFloat(newPayrollForm.deductions) || 0;

    if (salary < 0 || bonus < 0 || deductions < 0) {
      alert('Salary, bonus, and deductions cannot be negative');
      return;
    }

    // Validate 10-digit limit
    if (Math.floor(salary).toString().length > 10) {
      alert('Salary cannot exceed 10 digits');
      return;
    }
    if (Math.floor(bonus).toString().length > 10) {
      alert('Bonus cannot exceed 10 digits');
      return;
    }
    if (Math.floor(deductions).toString().length > 10) {
      alert('Deductions cannot exceed 10 digits');
      return;
    }

    // Validate date format and range
    const dateRegex = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (!dateRegex.test(newPayrollForm.payDate)) {
      alert('Pay date must be in DD-MM-YYYY format');
      return;
    }
    
    const parts = newPayrollForm.payDate.split('-');
    const year = parseInt(parts[2], 10);
    if (year < 2025 || year > 2099) {
      alert('Pay date must be between year 2025 and 2099');
      return;
    }

    const netPay = salary + bonus - deductions;

    const newId = Math.max(...payrollData.map(item => item.id), 0) + 1;
    
    const newPayrollEntry = {
      id: newId,
      employeeId: newPayrollForm.employeeId,
      name: newPayrollForm.name,
      department: newPayrollForm.department || 'Not Specified',
      position: newPayrollForm.position || 'Not Specified',
      salary: salary,
      bonus: bonus,
      deductions: deductions,
      netPay: netPay,
      status: newPayrollForm.status,
      payDate: newPayrollForm.payDate
    };

    setPayrollData(prev => [newPayrollEntry, ...prev]);
    setShowNewPayrollModal(false);
    
    // Reset form
    setNewPayrollForm({
      employeeId: '',
      name: '',
      department: '',
      position: '',
      salary: '',
      bonus: '',
      deductions: '',
      status: 'Pending',
      payDate: new Date().toLocaleDateString('en-GB').split('/').join('-')
    });
    
    alert(`New payroll for ${newPayrollForm.name} has been created successfully!`);
  };

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'Paid': return 'bg-green-100 text-green-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Processing': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // --- Summary Statistics (in Rupees) ---
  const totalPayrollThisMonth = payrollData.reduce((sum, item) => sum + item.netPay, 0);
  const totalPaidThisMonth = payrollData.filter(item => item.status === 'Paid').reduce((sum, item) => sum + item.netPay, 0);
  const pendingCount = payrollData.filter(item => item.status === 'Pending').length;

  // Pagination logic
  const filteredData = payrollData.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const itemsPerPage = 5;
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleViewDetails = (payroll) => {
    setSelectedPayroll(payroll);
    setShowDetailsModal(true);
  };

  const handleProcessPayroll = (id) => {
    setPayrollData(prevData =>
      prevData.map(item =>
        item.id === id ? { ...item, status: 'Processing', payDate: new Date().toLocaleDateString('en-GB').split('/').join('-') } : item
      )
    );
    alert(`Payroll for ID: ${id} is now processing.`);
  };

  const handleMarkAsPaid = (id) => {
    setPayrollData(prevData =>
      prevData.map(item =>
        item.id === id ? { ...item, status: 'Paid', payDate: new Date().toLocaleDateString('en-GB').split('/').join('-') } : item
      )
    );
    alert(`Payroll for ID: ${id} has been marked as paid.`);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans">
      {/* Header Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Payroll Management</h1>
        <p className="text-gray-500 mt-1">Manage employee compensation, bonuses, and payment records.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <p className="text-sm font-medium text-gray-500 uppercase">Total Payroll (All)</p>
          <p className="text-2xl font-bold text-gray-800">₹{totalPayrollThisMonth.toLocaleString('en-IN')}</p>
          <p className="text-xs text-gray-400 mt-1">All recorded payrolls</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <p className="text-sm font-medium text-gray-500 uppercase">Total Paid</p>
          <p className="text-2xl font-bold text-gray-800">₹{totalPaidThisMonth.toLocaleString('en-IN')}</p>
          <p className="text-xs text-green-600 mt-1">✓ Successfully disbursed</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
          <p className="text-sm font-medium text-gray-500 uppercase">Pending Approvals</p>
          <p className="text-2xl font-bold text-gray-800">{pendingCount}</p>
          <p className="text-xs text-yellow-600 mt-1">Awaiting processing</p>
        </div>
      </div>

      {/* Filters and Search Bar */}
      <div className="bg-white rounded-lg shadow mb-6 p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="relative flex-grow max-w-md">
          <input
            type="text"
            placeholder="Search by name, ID, or department..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <div className="flex gap-3">
          <select
            className="border border-gray-300 rounded-lg px-4 py-2 bg-white focus:ring-blue-500"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="All">All Status</option>
            <option value="Paid">Paid</option>
            <option value="Pending">Pending</option>
            <option value="Processing">Processing</option>
          </select>
          <button 
            onClick={() => setShowNewPayrollModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition shadow-sm"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Payroll
          </button>
        </div>
      </div>

      {/* Payroll Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dept / Position</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salary</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bonus</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deductions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Pay</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center font-semibold text-gray-600">
                          {item.name.charAt(0)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-sm text-gray-500">{item.employeeId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{item.department}</div>
                      <div className="text-sm text-gray-500">{item.position}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{item.salary.toLocaleString('en-IN')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">+₹{item.bonus.toLocaleString('en-IN')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">-₹{item.deductions.toLocaleString('en-IN')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">₹{item.netPay.toLocaleString('en-IN')}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(item.status)}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.payDate}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button onClick={() => handleViewDetails(item)} className="text-blue-600 hover:text-blue-900">View</button>
                        {item.status === 'Pending' && (
                          <button onClick={() => handleProcessPayroll(item.id)} className="text-yellow-600 hover:text-yellow-900">Process</button>
                        )}
                        {item.status === 'Processing' && (
                          <button onClick={() => handleMarkAsPaid(item.id)} className="text-green-600 hover:text-green-900">Pay</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                    No payroll records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Previous</button>
              <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Next</button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, filteredData.length)}</span> of{' '}
                  <span className="font-medium">{filteredData.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">
                    Previous
                  </button>
                  {[...Array(totalPages).keys()].map(number => (
                    <button key={number + 1} onClick={() => paginate(number + 1)} className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === number + 1 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
                      {number + 1}
                    </button>
                  ))}
                  <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* New Payroll Modal - With Validations */}
      {showNewPayrollModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b p-4 bg-gray-50 rounded-t-lg">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Create New Payroll</h3>
                <p className="text-sm text-gray-500 mt-1">Add a new employee payroll record</p>
              </div>
              <button onClick={() => setShowNewPayrollModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Employee Information */}
                <div className="col-span-2">
                  <h4 className="text-md font-semibold text-gray-700 mb-3 pb-2 border-b">Employee Information</h4>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee Name *
                    <span className="text-xs text-gray-400 ml-1">(max 100 chars)</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={newPayrollForm.name}
                    onChange={handleNewPayrollChange}
                    maxLength={100}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter full name"
                  />
                  <p className="text-xs text-gray-400 mt-1">{newPayrollForm.name.length}/100 characters</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee ID *
                    <span className="text-xs text-gray-400 ml-1">(max 15 chars)</span>
                  </label>
                  <input
                    type="text"
                    name="employeeId"
                    value={newPayrollForm.employeeId}
                    onChange={handleNewPayrollChange}
                    maxLength={15}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., E-1006"
                  />
                  <p className="text-xs text-gray-400 mt-1">{newPayrollForm.employeeId.length}/15 characters</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <select
                    name="department"
                    value={newPayrollForm.department}
                    onChange={handleNewPayrollChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Department</option>
                    <option value="Engineering">Engineering</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Sales">Sales</option>
                    <option value="HR">HR</option>
                    <option value="Finance">Finance</option>
                    <option value="Operations">Operations</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position
                    <span className="text-xs text-gray-400 ml-1">(max 100 chars)</span>
                  </label>
                  <input
                    type="text"
                    name="position"
                    value={newPayrollForm.position}
                    onChange={handleNewPayrollChange}
                    maxLength={100}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    placeholder="Job title"
                  />
                  <p className="text-xs text-gray-400 mt-1">{newPayrollForm.position.length}/100 characters</p>
                </div>
                
                {/* Compensation Details */}
                <div className="col-span-2">
                  <h4 className="text-md font-semibold text-gray-700 mb-3 pb-2 border-b mt-2">Compensation Details</h4>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base Salary (₹) *
                    <span className="text-xs text-gray-400 ml-1">(max 10 digits, non-negative)</span>
                  </label>
                  <input
                    type="number"
                    name="salary"
                    value={newPayrollForm.salary}
                    onChange={handleNewPayrollChange}
                    min="0"
                    step="1"
                    maxLength={10}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 75000"
                    onKeyDown={(e) => {
                      if (newPayrollForm.salary.toString().length >= 10 && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-1">Maximum 10 digits allowed</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bonus (₹)
                    <span className="text-xs text-gray-400 ml-1">(max 10 digits, non-negative)</span>
                  </label>
                  <input
                    type="number"
                    name="bonus"
                    value={newPayrollForm.bonus}
                    onChange={handleNewPayrollChange}
                    min="0"
                    step="1"
                    maxLength={10}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 5000"
                    onKeyDown={(e) => {
                      if (newPayrollForm.bonus.toString().length >= 10 && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-1">Maximum 10 digits allowed</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Deductions (₹)
                    <span className="text-xs text-gray-400 ml-1">(max 10 digits, non-negative)</span>
                  </label>
                  <input
                    type="number"
                    name="deductions"
                    value={newPayrollForm.deductions}
                    onChange={handleNewPayrollChange}
                    min="0"
                    step="1"
                    maxLength={10}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 3000"
                    onKeyDown={(e) => {
                      if (newPayrollForm.deductions.toString().length >= 10 && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-1">Maximum 10 digits allowed</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    name="status"
                    value={newPayrollForm.status}
                    onChange={handleNewPayrollChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Processing">Processing</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pay Date
                    <span className="text-xs text-gray-400 ml-1">(DD-MM-YYYY, 2025-2099)</span>
                  </label>
                  <input
                    type="text"
                    name="payDate"
                    value={newPayrollForm.payDate}
                    onChange={handleNewPayrollChange}
                    placeholder="DD-MM-YYYY"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Format: DD-MM-YYYY (e.g., 31-12-2025)</p>
                </div>
                
                {/* Calculated Net Pay Preview */}
                <div className="col-span-2 mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Calculated Net Pay:</span>
                    <span className="text-2xl font-bold text-blue-600">
                      ₹{(parseFloat(newPayrollForm.salary) || 0) + (parseFloat(newPayrollForm.bonus) || 0) - (parseFloat(newPayrollForm.deductions) || 0)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">* Net Pay = Base Salary + Bonus - Deductions (all values must be non-negative, max 10 digits)</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-b-lg flex justify-end gap-3">
              <button 
                onClick={() => setShowNewPayrollModal(false)} 
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmitNewPayroll} 
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm"
              >
                Create Payroll
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedPayroll && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center border-b p-4 bg-gray-50 rounded-t-lg">
              <h3 className="text-xl font-semibold text-gray-900">Payroll Details</h3>
              <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div><p className="text-sm text-gray-500">Employee Name</p><p className="font-medium">{selectedPayroll.name}</p></div>
              <div><p className="text-sm text-gray-500">Employee ID</p><p className="font-medium">{selectedPayroll.employeeId}</p></div>
              <div><p className="text-sm text-gray-500">Department</p><p className="font-medium">{selectedPayroll.department}</p></div>
              <div><p className="text-sm text-gray-500">Position</p><p className="font-medium">{selectedPayroll.position}</p></div>
              <div><p className="text-sm text-gray-500">Base Salary</p><p className="font-medium">₹{selectedPayroll.salary.toLocaleString('en-IN')}</p></div>
              <div><p className="text-sm text-gray-500">Bonus</p><p className="font-medium text-green-600">+₹{selectedPayroll.bonus.toLocaleString('en-IN')}</p></div>
              <div><p className="text-sm text-gray-500">Deductions</p><p className="font-medium text-red-600">-₹{selectedPayroll.deductions.toLocaleString('en-IN')}</p></div>
              <div><p className="text-sm text-gray-500">Net Pay</p><p className="font-bold text-lg">₹{selectedPayroll.netPay.toLocaleString('en-IN')}</p></div>
              <div><p className="text-sm text-gray-500">Status</p><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(selectedPayroll.status)}`}>{selectedPayroll.status}</span></div>
              <div><p className="text-sm text-gray-500">Pay Date</p><p className="font-medium">{selectedPayroll.payDate}</p></div>
            </div>
            <div className="bg-gray-50 p-4 rounded-b-lg flex justify-end gap-3">
              <button onClick={() => setShowDetailsModal(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">Close</button>
              {selectedPayroll.status === 'Pending' && <button onClick={() => { handleProcessPayroll(selectedPayroll.id); setShowDetailsModal(false); }} className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600">Process Payroll</button>}
              {selectedPayroll.status === 'Processing' && <button onClick={() => { handleMarkAsPaid(selectedPayroll.id); setShowDetailsModal(false); }} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Mark as Paid</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollSection;